import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import {
  House, PlusCircle, List, Car, ArrowCounterClockwise,
  FileText, GearSix, UserCircle, SignOut, UsersThree, CreditCard
} from "@phosphor-icons/react";
import { ScrollArea } from "@/components/ui/scroll-area";
import BrandLogo from "@/components/BrandLogo";

const NAV_ITEMS = [
  { path: "/dashboard", label: "Dashboard", icon: House },
  { path: "/rentals/new", label: "New Rental", icon: PlusCircle, accent: true },
  { path: "/rentals", label: "Rentals", icon: List },
  { path: "/fleet", label: "Fleet", icon: Car },
  { path: "/returns", label: "Returns", icon: ArrowCounterClockwise },
  { path: "/agreements", label: "Agreements", icon: FileText },
];

export default function Sidebar({ currentUser, className = "", onNavigate }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();

  const isActive = (path) => location.pathname === path || (path !== "/dashboard" && location.pathname.startsWith(path) && path !== "/rentals/new");
  const handleNavigate = (path) => {
    navigate(path);
    onNavigate?.();
  };

  return (
    <aside className={`flex flex-shrink-0 flex-col border-r border-border/80 bg-background/80 backdrop-blur-xl ${className}`.trim()} data-testid="sidebar">
      <div className="flex h-16 items-center gap-2.5 border-b border-border/80 px-5">
        <BrandLogo
          iconClassName="w-8 h-8 rounded-xl"
          textClassName="text-lg font-extrabold tracking-tight font-heading text-foreground"
        />
      </div>

      <ScrollArea className="flex-1">
        <div className="space-y-1 p-3">
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <button
                key={item.path}
                data-testid={`sidebar-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                onClick={() => handleNavigate(item.path)}
                className={`w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition-all duration-150 ${
                  item.accent && !active
                    ? "bg-primary text-primary-foreground hover:bg-primary/90"
                  : active
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-foreground/5 hover:text-foreground"
                }`}
              >
                <Icon weight={active || item.accent ? "bold" : "duotone"} className="w-5 h-5" />
                {item.label}
              </button>
            );
          })}

          {/* Organization */}
          {currentUser && currentUser._org_role !== "STAFF" && (
            <div className="pt-6">
              <p className="px-3 pb-2 text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground/70">
                Organization
              </p>
              <button
                data-testid="sidebar-team"
                onClick={() => handleNavigate("/team")}
                className={`w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition-all ${
                  location.pathname === "/team"
                    ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-foreground/5 hover:text-foreground"
                }`}
              >
                <UsersThree weight="duotone" className="w-5 h-5" />
                Team
              </button>
              <button
                data-testid="sidebar-billing"
                onClick={() => handleNavigate("/billing")}
                className={`w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition-all ${
                  location.pathname === "/billing"
                    ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-foreground/5 hover:text-foreground"
                }`}
              >
                <CreditCard weight="duotone" className="w-5 h-5" />
                Billing
              </button>
              <button
                data-testid="sidebar-settings"
                onClick={() => handleNavigate("/admin/settings")}
                className={`w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition-all ${
                  location.pathname.startsWith("/admin")
                    ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-foreground/5 hover:text-foreground"
                }`}
              >
                <GearSix weight="duotone" className="w-5 h-5" />
                Settings
              </button>
            </div>
          )}
        </div>
      </ScrollArea>

      {/* User footer */}
      <div className="space-y-1 border-t border-border/80 p-3">
        <button
          data-testid="sidebar-profile"
          onClick={() => handleNavigate("/profile")}
          className="w-full flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium text-muted-foreground hover:bg-foreground/5 hover:text-foreground"
        >
          <UserCircle weight="duotone" className="w-5 h-5" />
          <span className="truncate">{currentUser?.full_name || "Profile"}</span>
        </button>
        <button
          data-testid="sidebar-logout"
          onClick={() => {
            onNavigate?.();
            logout();
          }}
          className="w-full flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium text-muted-foreground/80 hover:bg-red-50 hover:text-red-600"
        >
          <SignOut weight="duotone" className="w-5 h-5" />
          Sign Out
        </button>
      </div>
    </aside>
  );
}
